import globals from 'scoreboard/components/globals'
import _ from 'lodash'
import DashboardData from '../dashboard-data'
import {
	isDatasetWidgetModel
} from 'scoreboard/dashboards/widgets/maestro'

class WidgetUpdater {
	constructor ({ canvas, fetchDataOptions, maestro }) {
		this.canvas = canvas
		this.fetchDataOptions = fetchDataOptions
		this.maestro = maestro
	}

	setProperty (name, value) {
		let update = {}
		update[name] = value
		this.setProperties(update)
	}

	setProperties (update) {
		this.recordHistory()

		// First get all selected widgets and the appropriate multi-updates for them
		let widgetsAndUpdates = this.selectedWidgets()
			.map(function (widget) {
				// WidgetModel.considerProperties() ensures that properties
				// are only set if they are appropriate for the given widget
				let updated = widget.considerProperties(update)
				return { widget, updated }
			})
			// Filter out any widgets that didn't end up being updated
			.filter(widgetAndUpdate => _.keys(widgetAndUpdate.updated).length > 0)

		if (widgetsAndUpdates.length === 0) {
			return
		}

		let widgetViewsByModelCid = this.widgetViewsByModelCid()
		let updateOnly = []
		let updateAndFetch = []

		widgetsAndUpdates.forEach(wu => {
			let nonFetchProps = widgetViewsByModelCid[wu.widget.cid].nonFetchProperties
			let staleData = _(wu.updated)
				.omit(nonFetchProps)
				.keys()
				.value().length > 0

			if (staleData) {
				updateAndFetch.push(wu)
			} else {
				updateOnly.push(wu)
			}
		})

		this.updateOnly(updateOnly)
		this.updateAndFetch(updateAndFetch)
	}

	updateOnly (widgetsAndUpdates) {
		if (_.isEmpty(widgetsAndUpdates)) {
			return
		}

		widgetsAndUpdates.forEach(wu => {
			wu.widget.properties.set(wu.updated)
		})
	}

	updateAndFetch (widgetsAndUpdates) {
		if (_.isEmpty(widgetsAndUpdates)) {
			return
		}

		widgetsAndUpdates.forEach(wu => {
			// Disable fetching on the widget's data before calling set(),
			// since we'll be fetching them all at once later
			wu.widget.data.disableFetch()
			wu.widget.properties.set(wu.updated)
		})

		// Create a DashboardData instance that we'll use to fetch just the
		// data for the updated widgets (instead of for the entire dashboard)
		let newData = new DashboardData({ fetchOptions: this.fetchDataOptions })

		newData.once('change', () => {
			widgetsAndUpdates.forEach(wu => {
				// Update the widget's DashboardData with the fresh data
				// and re-enable fetching for future changes
				wu.widget.data.set(newData)
				wu.widget.data.enableFetch()
			})
		})

		newData.fetch({
			calendarPeriodId: widgetsAndUpdates[0].widget.options.getPeriodId(),
			// Only fetch the updated widgets' data
			wobCids: widgetsAndUpdates.map(widgetAndUpdate => widgetAndUpdate.widget.wob.cid)
		})
	}

	/**
	 * For each selected widget, calls the given function. Note that the function
	 * is responsible for determining what (if any) changes should be applied
	 * to each widget (ie only setting thresholdMode on speedometers that
	 * support it).
	 *
	 * Does not currently trigger a data fetch, since it isn't required for any
	 * updates that use this, but that could be added in the future.
	 *
	 * @param {String[]} [options.types] - Only apply the update to widgets of
	 * the given types. If not provided, all widget types are updated
	 */
	applyUpdate (fn, opt = {}) {
		this.recordHistory()

		let widgetViewsByModelCid = this.widgetViewsByModelCid()

		this.selectedWidgets()
			.filter(widgetModel => opt.types ? _.includes(opt.types, widgetModel.properties.get('type')) : true)
			.forEach(widgetModel => {
				fn(widgetModel, widgetViewsByModelCid[widgetModel.cid])
			})
	}

	/**
	 * For each selected dataset widget, calls the given function.  Note that the function
	 * is responsible for determining what (if any) changes should be applied
	 * to each widget (ie only applying aggregationType() to value widgets)
	 *
	 * If the function returns true for a given widget, a dataset query will be done.
	 */
	applyDatasetUpdate (fn, opt = {}) {
		this.recordHistory()

		let widgetViewsByModelCid = this.widgetViewsByModelCid()

		let fetchesByDatasetId = _(this.selectedWidgets())
			.filter(widgetModel => {
				return opt.types
					? _.includes(opt.types, widgetModel.properties.get('type'))
					: isDatasetWidgetModel(widgetModel)
			})
			.filter(widgetModel => {
				let requiresFetch = fn(widgetModel)
				return requiresFetch
			})
			.groupBy(widgetModel => widgetModel.properties.get('datasetId'))
			.value()

		_.toPairs(fetchesByDatasetId).forEach(([ datasetId, widgetModels ]) => {
			this.maestro.query(datasetId, widgetModels.map(m => widgetViewsByModelCid[m.cid]))
		})
	}

	/**
	 * Triggers a dataset query for the given widget model after it has been
	 * manually updated. This is appropriate for scenarios where a dataset widget
	 * is updated, but we don't want to apply it to other selected widgets.
	 */
	datasetWidgetUpdated (widgetModel) {
		let datasetId = widgetModel.properties.get('datasetId')
			?? widgetModel.datasetId?.()
		let view = this.widgetViewsByModelCid()[widgetModel.cid]

		this.maestro.query(datasetId, view)
	}

	widgetViewsByModelCid () {
		return _(this.canvas.views)
			.values()
			.map(wobView => wobView.view)
			.keyBy(widgetView => widgetView.model.cid)
			.value()
	}

	selectedWidgets () {
		return this.canvas.selection.wobs.map(selected => selected.widget)
	}

	recordHistory () {
		this.canvas.history.record()
	}
}

export default WidgetUpdater

WidgetUpdater.__filename__ = 'dashboards/edit/widget-updater.js'
